/* eslint-disable react-hooks/exhaustive-deps */
import AntdIcon, { LoadingOutlined, MenuOutlined, ReloadOutlined } from '@ant-design/icons'
import { Checkbox, Input, Layout, notification, Select, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { CheckIcon, LanguagesIcon } from 'lucide-react'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { FlagIcon } from 'react-flag-kit'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { AuthApi, QUERY_KEYS } from '@api'
import { config } from '@config'
import { LOCAL_STORAGE_KEYS, ROUTE_KEYS } from '@constants'
import { useContractContracts, useGeneral, useUser } from '@contexts'
import { LogoExiros } from '@icons'
import { ComboOption, NotificationEnum, RoleEnum, SVGIcon } from '@types'

import '../../firebase/firebase'
import i18n from '../../i18n'
import { theme } from '../../styles'
import { useContractsUpdate } from '../ContractsDashboard/hooks/useContractsUpdate'
import { contractUpdateApi } from '../ContractsDashboard/hooks/useContractsUpdate/api'
import { FlexRow, StyledFont } from '../StyledComponents'
import UserInfo from '../UserInfo'
import {
  ContainerButtons,
  ContainerCheckbox,
  JobUpdateContainer,
  LogoContainer,
  SearchContainer,
  StyledButton,
  StyledCheckboxGroup,
  StyledContainer,
  StyledHeader,
  StyledSider,
  UserColor,
} from './components'

// TODO:  delete when all dashboards are refactored
interface DashboardLayoutProps {
  buttons?: {
    name: string
    primary: boolean
    svgColor: string
    icon: typeof AntdIcon | SVGIcon
    action: () => void
  }[]
  checkboxesGroup?: {
    name: string
    checkboxes: string[]
  }[]
  menu?: () => JSX.Element
  searchSelectOptions: ComboOption<string>[]
}

type FlagIconCode = 'GB' | 'RO'

const languages = [
  { label: 'English', value: 'en', flag: 'GB' },
  { label: 'Română', value: 'ro', flag: 'RO' },
]

const lngs = languages.map(({ value }) => value)

const lng = lngs.includes(i18n.language) ? i18n.language : lngs[0]

const messaging = getMessaging()
let firebaseTokenSaved = false

const DashboardLayout: React.FC<PropsWithChildren<DashboardLayoutProps>> = ({
  checkboxesGroup,
  buttons,
  children,
  menu,
  searchSelectOptions,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state: user } = useUser()
  const queryClient = useQueryClient()
  const { menuDisplayed, handleDisplayMenu, setTicketToSearch } = useGeneral()
  const roleUser = user?.roles[0].name
  const roles = user?.roles.map(role => role.name)
  const { ADMIN, SUPPLIER, CARRIER, PROCUREMENT, ADMINRO } = RoleEnum
  const { pathname } = useLocation()
  const { contractUpdate, isContractLoading, updateContracts } = useContractsUpdate()
  const { isContractsSync, setIsContractsSync } = useContractContracts()
  const [searchInputValue, setSearchInputValue] = useState<undefined | string>()
  const [filterSelectSelected, setFilterSelectSelected] = useState(searchSelectOptions[0].value)
  const notificationKey = 'loginNotificationShown'
  const [selectedLng, setSelectedLng] = useState<string>()
  const hasNotificationBeenShown = () => {
    return localStorage.getItem(notificationKey) === 'true'
  }
  const markNotificationAsShown = () => {
    localStorage.setItem(notificationKey, 'true')
  }

  const handleUpdateContracts = async () => {
    setIsContractsSync(true)
    notification.info({
      message: t('globals.synchronizing_contracts'),
      key: 'synchronizingContracts',
    })
    try {
      await contractUpdateApi.syncContracts()
      queryClient.resetQueries([QUERY_KEYS.CONTRACTS])
      notification.destroy('synchronizingContracts')
      notification.success({
        message: t('globals.contracts_synchronized'),
      })
      updateContracts()
      setIsContractsSync(false)
    } catch {
      notification.error({
        message: t('globals.error_sync_contracts'),
      })
      setIsContractsSync(false)
    }
  }

  const contractUrl = pathname.includes('contract')
  const routesUrl = pathname.includes('routes')
  const settingsUrl = pathname.includes('settings')
  const simulationUrl = pathname.includes('simulation')
  const batcheesnUrl = pathname.includes('batchees')
  const carriersAdminViewUrl = pathname.includes('carriers')
  const vehiclesAdminViewUrl = pathname.includes('vehicles')
  const driversAdminViewUrl = pathname.includes('drivers')
  const trailersAdminViewUrl = pathname.includes('trailers')
  const openAdminViewUrl = pathname.includes('open')
  const closedAdminViewUrl = pathname.includes('closed')
  const certifiedAdminViewUrl = pathname.includes('certified')
  getToken(messaging, {
    vapidKey: config.vapidKey,
  })
    .then(firebaseToken => {
      if (user?.id && !firebaseTokenSaved) {
        AuthApi.saveFirebaseToken(user.id, firebaseToken)
        firebaseTokenSaved = true
        window.localStorage.setItem(LOCAL_STORAGE_KEYS.FIREBASE, firebaseToken)
      }
    })
    .catch(() => {
      if (!hasNotificationBeenShown()) {
        notification.success({
          message: t('globals.push_notifications_info'),
          duration: 3,
          key: 'notification',
        })

        markNotificationAsShown()
      }
    })
  onMessage(messaging, payload => {
    switch (payload.data?.type) {
      case NotificationEnum.NEW_TICKET_ADMIN:
        if (roles?.includes(ADMIN)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.supplierName} ha creado una nueva solicitud #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        if (roles?.includes(ADMINRO)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.supplierName} ha creado una nueva solicitud #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.DECLINED:
        if (roles?.includes(ADMIN)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.carrierName} ha cancelado el servicio #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        if (roles?.includes(ADMINRO)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.carrierName} ha cancelado el servicio #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.NEW_TICKET_CARRIER:
        if (roles?.includes(CARRIER)) {
          queryClient.refetchQueries([QUERY_KEYS.PENDINGS, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `Tiene una nueva solicitud #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.NEW_TICKET_SUPPLIER:
        if (roles?.includes(SUPPLIER)) {
          queryClient.refetchQueries([QUERY_KEYS.TICKETS])
          return notification.success({
            message: `La administración creó una nueva solicitud #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.AUTHORIZED:
        if (roleUser === SUPPLIER) {
          queryClient.refetchQueries([QUERY_KEYS.RAILCAR])
          return notification.success({
            message: `${t('notifications.push.approved')} #${payload.data?.blockTrainId}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.TRANSIT:
        if (roleUser === ADMIN) {
          queryClient.refetchQueries([QUERY_KEYS.RAILCAR])
          return notification.success({
            message: t('notifications.push.transit', {
              blockTrainId: payload.data?.blockTrainId,
            }),
            duration: 0,
          })
        }
        return null
      case NotificationEnum.WAITING_APPROVAL:
        if (roleUser === ADMIN) {
          queryClient.refetchQueries([QUERY_KEYS.RAILCAR])
          return notification.success({
            message: t('notifications.push.waiting_for_approval', {
              blockTrainId: payload.data?.blockTrainId,
            }),
            duration: 0,
          })
        }
        return null
      case NotificationEnum.CONFIRMED:
        queryClient.refetchQueries([QUERY_KEYS.TICKETS])
        return notification.success({
          message: `Tren ID ${payload.data?.blockTrainId}: ${payload.data?.title} by ${payload.data?.carrierName}`,
          duration: 0,
        })
      case NotificationEnum.CANCELLED_REALLOCATED_TICKET:
        queryClient.refetchQueries([QUERY_KEYS.TICKETS])
        return notification.info({
          message: `Ticket desasignado. ID #${payload.data?.blockTrainId}`,
          duration: 0,
        })
      default:
        return notification.success({ message: 'Nueva información sin identificar' })
    }
  })

  const handleSearch = (value: string) => {
    setTicketToSearch(value)
    navigate(
      ROUTE_KEYS[user!.roles[0].name as RoleEnum.ADMIN | RoleEnum.ADMINRO | RoleEnum.SUPPLIER]
        .BLOCKTRAINS,
    )
  }

  const handleChangeSelect = (value: string) => {
    setSearchInputValue(undefined)
    setFilterSelectSelected(value)
  }

  const onLogout = () => {
    firebaseTokenSaved = false
  }

  useEffect(() => {
    if (selectedLng) {
      i18n.changeLanguage(selectedLng)
    } else {
      setSelectedLng(lng)
    }
  }, [selectedLng])

  useEffect(() => {
    handleDisplayMenu(window.innerWidth >= theme.intSize.md)
  }, [])

  return (
    <Layout style={{ height: '100vh' }}>
      <StyledHeader>
        <LogoContainer>
          <MenuOutlined
            className="displayer"
            onClick={() => {
              handleDisplayMenu()
            }}
          />
          <LogoExiros />
        </LogoContainer>
        {roles?.includes(PROCUREMENT) ||
        contractUrl ||
        settingsUrl ||
        routesUrl ||
        batcheesnUrl ||
        carriersAdminViewUrl ||
        driversAdminViewUrl ||
        vehiclesAdminViewUrl ||
        trailersAdminViewUrl ||
        openAdminViewUrl ||
        closedAdminViewUrl ||
        certifiedAdminViewUrl ||
        simulationUrl ? null : (
          <>
            <SearchContainer style={{ flex: 1 }}>
              <Select
                value={filterSelectSelected}
                onChange={handleChangeSelect}
                className="search-type"
              >
                {searchSelectOptions.map(({ label, value }) => (
                  <Select.Option key={label + value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
              <Input.Search
                className="ticket-search-input"
                placeholder={t('search')}
                type="string"
                onSearch={handleSearch}
                value={searchInputValue}
                onChange={({ target }) => setSearchInputValue(target.value)}
                allowClear
              />
            </SearchContainer>
          </>
        )}
        {contractUrl && (
          <JobUpdateContainer>
            <p>{t('admin_router.admin_dashboard.contracts.job_update')}</p>
            <StyledFont>
              <p>{dayjs(contractUpdate?.createAt).format('DD/MM/YYYY HH:mm')}</p>
            </StyledFont>
            <Tooltip
              title={
                isContractsSync || isContractLoading
                  ? t('globals.synchronizing_contracts')
                  : t('globals.tooltips.sync_contracts')
              }
            >
              {isContractsSync || isContractLoading ? (
                <LoadingOutlined className="load-icon" />
              ) : (
                <ReloadOutlined className="load-icon" onClick={handleUpdateContracts} />
              )}
            </Tooltip>
          </JobUpdateContainer>
        )}
        <div className="header-user-info">
          <UserInfo onLogout={onLogout} />
        </div>
      </StyledHeader>
      <UserColor role={roleUser} />
      <Layout style={{ position: 'relative' }}>
        <StyledSider
          collapsed={!menuDisplayed}
          collapsedWidth={0}
          className={menuDisplayed ? 'menu-displayed' : undefined}
        >
          {menu && menu()}
          <>
            {buttons && (
              <ContainerButtons>
                {buttons.map(button => (
                  <StyledButton
                    type={button.primary ? 'primary' : 'default'}
                    svgColor={button.svgColor}
                    block
                    onClick={button.action}
                    icon={<button.icon />}
                  >
                    {button.name}
                  </StyledButton>
                ))}
              </ContainerButtons>
            )}
            {checkboxesGroup?.map(({ name, checkboxes }) => (
              <StyledCheckboxGroup>
                <p>{name}</p>
                {checkboxes.map(checkbox => (
                  <ContainerCheckbox span={24}>
                    <Checkbox value={checkbox}>{checkbox}</Checkbox>
                  </ContainerCheckbox>
                ))}
              </StyledCheckboxGroup>
            ))}
          </>
          <div className="menu-user-info">
            <UserInfo onLogout={onLogout} />
          </div>
          <FlexRow
            style={{
              justifyContent: 'center',
              padding: '0.75rem',
              backgroundColor: theme.white,
              borderInlineEnd: '1px solid rgba(5, 5, 5, 0.06)',
            }}
          >
            <Select
              value={selectedLng}
              onChange={setSelectedLng}
              suffixIcon={<LanguagesIcon />}
              menuItemSelectedIcon={<CheckIcon />}
              style={{ width: '100%' }}
            >
              {languages.map(({ value, label, flag }) => (
                <Select.Option value={value} key={value}>
                  <FlexRow style={{ gap: '0.375rem', alignItems: 'center' }}>
                    <FlagIcon size={20} height={16} code={flag as FlagIconCode} />
                    <Typography.Text>{label}</Typography.Text>
                  </FlexRow>
                </Select.Option>
              ))}
            </Select>
          </FlexRow>
        </StyledSider>
        <StyledContainer>{children}</StyledContainer>
      </Layout>
    </Layout>
  )
}

export { DashboardLayout }
